/* eslint-disable max-len */
import React, { useState } from 'react';
import {
  Box, Button, Divider, List, ListItem, ListItemButton, ListItemText, TextField, Typography,
} from '@mui/material';
import { DigitalQuestion, DigitalQuestionScore } from '../../../redux/types';
import GenericDialog from '../../GenericDialog';

type Props = {
  questions: DigitalQuestion[];
  questionScores?: DigitalQuestionScore[];
  selectedGroundTruthGroupId?: number;
  addOrUpdateDigitalQuestionScore: (payload: Omit<DigitalQuestionScore, 'id'>) => void;
};

const QuestionScoreEditor: React.FC<Props> = ({
  questions,
  questionScores,
  selectedGroundTruthGroupId,
  addOrUpdateDigitalQuestionScore,
}: Props) => {
  const [questionScoreBuffer, setQuestionScoreBuffer] = useState<DigitalQuestionScore & {
    passScoreBuffer?: string;
    passScoreError?: boolean;
  }>();

  const onOpenDialog = (qScore?: DigitalQuestionScore) => {
    if (!qScore) {
      return;
    }
    setQuestionScoreBuffer({
      ...qScore,
      passScoreBuffer: qScore.passScore.toString(),
    });
  };

  const onCloseDialog = () => {
    setQuestionScoreBuffer(undefined);
  };

  const onPassScoreChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!questionScoreBuffer) {
      return;
    }
    const text = e.target.value;
    setQuestionScoreBuffer({
      ...questionScoreBuffer,
      passScoreBuffer: text,
    });
  };

  const onSaveScore = () => {
    if (!questionScoreBuffer) {
      return;
    }
    if (questionScoreBuffer.passScoreBuffer
        && /\d/.test(questionScoreBuffer.passScoreBuffer)
        && Number(questionScoreBuffer.passScoreBuffer) <= questionScoreBuffer.maxScore) {
      addOrUpdateDigitalQuestionScore({
        ...questionScoreBuffer,
        passScore: Number(questionScoreBuffer.passScoreBuffer),
      });
      onCloseDialog();
    } else {
      setQuestionScoreBuffer({
        ...questionScoreBuffer,
        passScoreError: true,
      });
    }
  };

  const questionScoresMap = questionScores && questionScores
    .filter((qs) => qs.digitalGroundTruthGroupId === selectedGroundTruthGroupId)
    .reduce((acc, cur) => {
      acc[cur.digitalQuestionId] = cur;
      return acc;
    }, {} as Record<number, DigitalQuestionScore>);

  if (selectedGroundTruthGroupId === undefined) {
    return null;
  }

  return (
    <Box>
      <Typography sx={{ mx: 2 }} fontWeight={500}>
        Question Scores
      </Typography>
      <List>
        {
          questions.map((q) => (
            <React.Fragment key={q.id}>
              <ListItem disablePadding>
                <ListItemButton onClick={() => onOpenDialog(questionScoresMap?.[q.id])}>
                  <ListItemText
                    primary={q.text}
                    secondary={!!questionScoresMap && (
                      <Box component="span" display="flex">
                        <Box component="span" display="flex" gap={1} flexBasis={120}>
                          <Typography component="span" variant="body2">
                            Pass Score:
                          </Typography>
                          <Typography component="span" variant="body2">
                            {questionScoresMap[q.id]?.passScore !== undefined ? questionScoresMap[q.id]?.passScore : '-'}
                          </Typography>
                        </Box>
                        <Box component="span" display="flex" gap={1} flexBasis={200}>
                          <Typography component="span" variant="body2">
                            Possible Max Score:
                          </Typography>
                          <Typography component="span" variant="body2">
                            {questionScoresMap[q.id]?.maxScore !== undefined ? questionScoresMap[q.id]?.maxScore : '-'}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  />
                </ListItemButton>
              </ListItem>
            </React.Fragment>
          ))
        }

      </List>
      <GenericDialog
        isOpen={questionScoreBuffer !== undefined}
        onRequestClose={onCloseDialog}
        maxWidth="sm"
      >
        <Box sx={{ p: 2 }}>
          <Typography component="div" variant="h5" sx={{ mb: 2 }}>Question Score Settings</Typography>
          <Box display="flex" gap={2} sx={{ mx: 2, my: 4 }}>
            <TextField
              value={questionScoreBuffer?.passScoreBuffer !== undefined ? questionScoreBuffer?.passScoreBuffer : ''}
              label="Pass score"
              onChange={onPassScoreChange}
              error={questionScoreBuffer?.passScoreError}
            />
            <TextField
              value={questionScoreBuffer?.maxScore !== undefined ? questionScoreBuffer?.maxScore : ''}
              label="Possible Maximum Score"
              onChange={onPassScoreChange}
              disabled
            />
          </Box>
          <Divider />
          <Box sx={{ mt: 2 }} justifyContent="flex-end" display="flex" gap={1}>
            <Button onClick={onCloseDialog} color="error" variant="contained">Cancel</Button>
            <Button onClick={onSaveScore} variant="contained">Save</Button>
          </Box>
        </Box>
      </GenericDialog>
    </Box>
  );
};

export default QuestionScoreEditor;
